import React from 'react';
import { IconHandsHelping, IconUniversity, IconBriefcase, IconCity, IconPlus } from './icons';

/**
 * 🔁 CARROUSEL DÉFILANT DES PARTENAIRES (bande de logos en défilement continu)
 * - partners : [{ name, logo, website, type }] (src/data/partners.js) ; sans logo, les initiales s'affichent.
 * - Sans partenaire, la bande fait défiler des emplacements « Votre logo ici » par type de partenaire :
 *   aucun nom ni logo d'organisation n'est affiché sans convention signée.
 * Le défilement s'arrête au survol ou au clavier, et laisse place à une simple rangée si l'appareil
 * demande moins d'animations (styles .marquee dans src/index.css).
 */

const PLACEHOLDERS = [
  { type: 'ONG et associations', icon: IconHandsHelping },
  { type: 'Microfinance et banques', icon: IconUniversity },
  { type: 'Entreprises', icon: IconBriefcase },
  { type: 'Collectivités et institutions', icon: IconCity }
];

const initials = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(word => word[0]).join('').toUpperCase();

const LogoTile = ({ partner }) => {
  const content = partner.logo
    ? <img src={partner.logo} alt={partner.name} loading="lazy" className="max-h-12 max-w-[9rem] object-contain grayscale transition duration-300 group-hover:grayscale-0" />
    : (
      <span className="flex items-center gap-3">
        <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-blue-700 to-green-600 text-sm font-black text-white">{initials(partner.name)}</span>
        <span className="text-left text-sm font-semibold leading-tight text-gray-800">{partner.name}</span>
      </span>
    );
  const className = 'group flex h-24 w-52 flex-none items-center justify-center rounded-2xl bg-white px-5 shadow-sm ring-1 ring-gray-200 transition-shadow hover:shadow-md';
  return partner.website
    ? <a href={partner.website} target="_blank" rel="noopener noreferrer" title={partner.name} className={className}>{content}</a>
    : <div title={partner.name} className={className}>{content}</div>;
};

const PlaceholderTile = ({ item }) => (
  <div className="flex h-24 w-52 flex-none items-center gap-3 rounded-2xl border-2 border-dashed border-blue-200 bg-white/70 px-4 text-left">
    <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-blue-50 text-blue-700">
      <item.icon className="h-5 w-5" aria-hidden="true" />
    </span>
    <span className="min-w-0">
      <span className="flex items-center gap-1 text-sm font-bold text-gray-900"><IconPlus className="h-3.5 w-3.5 text-orange-500" aria-hidden="true" /> Votre logo ici</span>
      <span className="block text-xs leading-snug text-gray-500">{item.type}</span>
    </span>
  </div>
);

const PartnerMarquee = ({ partners = [], speed = 40 }) => {
  const hasPartners = partners.length > 0;
  const tiles = hasPartners
    ? partners.map(partner => <LogoTile key={partner.name} partner={partner} />)
    : PLACEHOLDERS.map(item => <PlaceholderTile key={item.type} item={item} />);
  // Bande assez longue pour remplir les grands écrans, puis doublée pour un défilement sans couture
  const repeat = Math.max(1, Math.ceil(8 / tiles.length));
  const band = Array.from({ length: repeat }, (_, round) => tiles.map(tile => React.cloneElement(tile, { key: `${round}-${tile.key}` }))).flat();

  return (
    <div className="marquee relative overflow-hidden py-2" style={{ '--marquee-duration': `${speed}s` }}
      aria-label={hasPartners ? 'Nos partenaires' : 'Emplacements réservés à nos futurs partenaires'} role="region">
      {/* Fondu sur les bords */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-[var(--marquee-bg,#fff)] to-transparent sm:w-24" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-[var(--marquee-bg,#fff)] to-transparent sm:w-24" aria-hidden="true" />
      <div className="marquee-track flex">
        <div className="flex gap-5">{band}</div>
        <div className="flex gap-5" aria-hidden="true">{band.map(tile => React.cloneElement(tile, { key: `copie-${tile.key}` }))}</div>
      </div>
    </div>
  );
};

export default PartnerMarquee;
